import { useEffect, useRef, useState } from "react";

import { obterPosicoes, obterSerie, obterStatus } from "../api/client";
import type { Piloto } from "../lib/pilotos";
import type { PositionPoint, Series, StatusPoint } from "../types";

/** O que o replay precisa de cada boia do piloto. */
export interface DadosBoiaPiloto {
  /** Fixes GNSS por ordem cronológica. Vazio = a boia nunca reportou posição. */
  rasto: PositionPoint[];
  temperatura: Series | null;
  /** `status` chega ~1/min — cabe inteiro em qualquer piloto de um dia. */
  status: StatusPoint[];
}

export interface DadosPiloto {
  por: Record<string, DadosBoiaPiloto>;
  erro: string | null;
  aCarregar: boolean;
}

const VAZIO: DadosPiloto = { por: {}, erro: null, aCarregar: false };

/**
 * Dados de um piloto anterior, para o mapa e a barra de replay.
 *
 * Separado do `useHistory` do painel: este pede **posições** (o painel não) e
 * pede-as para todas as boias do elenco de uma vez, porque no replay os rastos
 * aparecem todos — a regra de "um rasto de cada vez" do modo ao vivo existe
 * para seis boias vivas num pentágono; aqui são três, em sítios diferentes.
 *
 * Uma janela de piloto é passado fechado: pede-se uma vez por piloto e nunca
 * se refresca.
 */
export function usePiloto(piloto: Piloto | null): DadosPiloto {
  const [dados, setDados] = useState<DadosPiloto>(VAZIO);
  const abortar = useRef<AbortController | null>(null);

  const id = piloto?.id ?? null;

  useEffect(() => {
    if (piloto === null) {
      setDados(VAZIO);
      return;
    }

    abortar.current?.abort();
    const controlo = new AbortController();
    abortar.current = controlo;
    const de = new Date(piloto.de);
    const ate = new Date(piloto.ate);

    setDados((a) => ({ ...a, aCarregar: true }));

    void (async () => {
      try {
        const resultados = await Promise.all(
          piloto.boias.map(async (boia): Promise<[string, DadosBoiaPiloto]> => {
            const [posicoes, temperatura, status] = await Promise.all([
              boia.gnss
                ? obterPosicoes(boia.code, de, ate, controlo.signal)
                : Promise.resolve({ itens: [], truncado: false }),
              obterSerie(boia.code, "temperature", de, ate, controlo.signal),
              obterStatus(boia.code, de, ate, controlo.signal),
            ]);
            // Só se deitam fora fixes **impossíveis** (hdop absurdo — houve um
            // com 500 e 2 satélites no piloto de setembro). O resto do ruído
            // fica: ±20 m de CGPSINFO é o que o sensor deu, e alisá-lo aqui
            // era inventar um rasto que não foi medido.
            const rasto = posicoes.itens.filter((p) => p.hdop === null || p.hdop <= 10);
            return [boia.code, { rasto, temperatura, status: status.itens }];
          }),
        );
        setDados({ por: Object.fromEntries(resultados), erro: null, aCarregar: false });
      } catch (erro) {
        if (controlo.signal.aborted) return;
        setDados({
          por: {},
          erro: erro instanceof Error ? erro.message : "Falha a ler os dados do piloto",
          aCarregar: false,
        });
      }
    })();

    return () => controlo.abort();
    // `piloto` é imutável por id — o objeto vem de uma constante do registo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return dados;
}
